import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';
import { ConfigSchema } from './division';

/**
 * Registration Schema
 * Represents a registration period for a league/tournament/season
 */

// Cost sub-schema
export const CostSchema = z.object({
  amount: z.number().optional(),
  perPlayer: z.boolean().optional(),
  perTeam: z.boolean().optional(),
});

export type Cost = z.infer<typeof CostSchema>;

// Settings sub-schema
export const RegistrationSettingsSchema = z.object({
  numDaysToRemindTeam: z.number().optional(),
  emailReminderTeam: z.boolean().optional(),
  rosterLockDate: z.coerce.date().optional(),
  rosterLock: z.boolean().optional(),
});

export type RegistrationSettings = z.infer<typeof RegistrationSettingsSchema>;

// Field error sub-schema
export const FieldErrorSchema = z.object({
  name: z.string().optional(),
  helperText: z.string().optional(),
});

// Registration field sub-schema (used for teamFields, managerFields, fields)
export const RegistrationFieldSchema = z.object({
  name: z.string().optional(),
  fieldId: z.string().optional(),
  required: z.boolean().optional(),
  helperText: z.string().optional(),
  order: z.number().optional(),
  primitiveType: z.string().optional(),
  fieldType: z.string().optional(),
  permanent: z.boolean().optional(),
  pinned: z.boolean().optional(),
  include: z.boolean().optional(),
  options: z.array(z.string()).default([]),
  error: FieldErrorSchema.optional(),
});

export type RegistrationField = z.infer<typeof RegistrationFieldSchema>;

// Player waiver sub-schema
export const PlayerWaiverSchema = z.object({
  required: z.boolean().optional(),
  waiver: objectId,
});

export type PlayerWaiver = z.infer<typeof PlayerWaiverSchema>;

export const RegistrationSchema = z.object({
  _id: objectId,
  message: z.string().optional(),
  accessCode: z.string().optional(),
  requireReleaseStatement: z.boolean().optional(), // Team waiver
  releaseStatement: z.string().optional(),
  hasPayments: z.boolean().optional(),
  allowCoaches: z.boolean().optional(),
  requirePlayers: z.boolean().optional(),
  hidden: z.boolean().default(false),
  contestType: z.string().default(''),
  active: z.boolean().default(true),
  requireThankYouMessage: z.boolean().default(false),
  thankYouMessage: z.string().default(''),
  startDate: z.coerce.date().default(() => new Date()),
  deadlineDate: z.coerce.date().default(() => new Date()),
  // Registration close date. Optional (unlike startDate, which defaults to now)
  // so the mere presence of the field doesn't imply a deadline — the
  // config.gateByEndDate flag turns the close gate on. Stored as an absolute
  // instant; interpreted/displayed in `timeZone`. See registrationSchedule.ts.
  endDate: z.coerce.date().optional(),
  // IANA time zone (e.g. "America/Los_Angeles") the admin picked for this
  // registration's start/end date+times. Governs how the wall-clock the admin
  // types is interpreted and how the open/close times are displayed. The gate
  // itself compares absolute instants, so this does NOT affect open/closed math.
  timeZone: z.string().optional(),
  // Nested objects
  cost: CostSchema.optional(),
  config: ConfigSchema.optional(),
  settings: RegistrationSettingsSchema.optional(),
  sports: z.array(z.string()).default([]),
  // Arrays of sub-documents
  teamFields: z.array(RegistrationFieldSchema).default([]),
  managerFields: z.array(RegistrationFieldSchema).default([]),
  fields: z.array(RegistrationFieldSchema).default([]),
  playerWaivers: z.array(PlayerWaiverSchema).default([]),
  // Refs
  organization: objectId,
  tournament: objectId,
  league: objectId,
  // DEPRECATED (ADR 0034): the fee/donation now live on the parent
  // tournament/contest `config.paymentProduct`/`config.donationProduct` and the
  // registration is NOT a resolution tier. These fields remain declared only so
  // legacy documents hydrate; nothing reads or writes them anymore.
  paymentProduct: objectId,
  donationProduct: objectId,
  contest: objectId, // League ref
  contests: z.array(z.string()).default([]), // Array of League refs
  ...timestampFields,
});

export type Registration = z.infer<typeof RegistrationSchema>;

// Metadata for Registration fields
export const registrationMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  message: { label: 'Message', editable: true },
  accessCode: { label: 'Access Code', editable: true },
  requireReleaseStatement: { label: 'Require Release Statement', editable: true },
  releaseStatement: { label: 'Release Statement', editable: true },
  hasPayments: { label: 'Has Payments', editable: true },
  allowCoaches: { label: 'Allow Coaches', editable: true },
  requirePlayers: { label: 'Require Players', editable: true },
  hidden: { label: 'Hidden', editable: true },
  contestType: { label: 'Contest Type', editable: true },
  active: { label: 'Active', editable: true },
  requireThankYouMessage: { label: 'Require Thank You Message', editable: true },
  thankYouMessage: { label: 'Thank You Message', editable: true },
  startDate: { label: 'Start Date', editable: true },
  deadlineDate: { label: 'Deadline Date', editable: true },
  endDate: { label: 'End Date', editable: true },
  timeZone: { label: 'Time Zone', editable: true },
  cost: { label: 'Cost' },
  config: { label: 'Config', hidden: true },
  settings: { label: 'Settings', hidden: true },
  sports: { label: 'Sports', editable: true },
  teamFields: { label: 'Team Fields' },
  managerFields: { label: 'Manager Fields' },
  fields: { label: 'Fields' },
  playerWaivers: { label: 'Player Waivers' },
  organization: { ref: 'Organization', hidden: true },
  tournament: { ref: 'Tournament', label: 'Tournament' },
  league: { ref: 'League', label: 'League' },
  paymentProduct: { ref: 'PaymentProduct', label: 'Payment Product' },
  donationProduct: { ref: 'PaymentProduct', label: 'Donation Product' },
  contest: { ref: 'League', label: 'Contest' },
  contests: { ref: 'League', label: 'Contests' },
  created: { hidden: true },
  updated: { hidden: true },
};

// Metadata for playerWaiver sub-document
export const playerWaiverMetadata: Record<string, FieldMetadata> = {
  waiver: { ref: 'Waiver' },
};
